import { Button } from '@mui/material';
import { GridToolbar, GridToolbarContainer } from '@mui/x-data-grid';
import dayjs from 'dayjs';
import { Workbook } from 'exceljs';
import saveAs from 'file-saver';

interface Row {
  id: number;
  [key: string]: any;
}
interface ColumnMapping {
  [originalColumnName: string]: string;
}
interface CustomToolbarProps {
  rows: Row[];
  columnMapping: ColumnMapping;
  title: string;
  eliminarTodos: () => void;
}

export const capitalize = (str: string) => {
  return str.replace(/\w\S*/g, function (word) {
    return word.charAt(0).toUpperCase() + word.substr(1).toLowerCase();
  });
};

const CustomToolbar: React.FC<CustomToolbarProps> = ({ rows, columnMapping, title, eliminarTodos }) => {
  const exportToExcel = async () => {
    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet('Datos');

    // Añadir título
    const titleRow = worksheet.addRow([title]);
    titleRow.font = { bold: true, size: 18, color: { argb: 'FF217dbb' } };

    // Modificar los encabezados para incluir "Nombre y Apellido"
    const headers = Object.values(columnMapping).map(header => {
      return header === 'Apellido' || header === 'Nombre' ? 'Nombre y Apellido' : header;
    }).filter((header, index, array) => array.indexOf(header) === index); // Eliminar duplicados
    const headerRow = worksheet.addRow(headers);
    headerRow.eachCell((cell) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF217dbb' },
      };
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      cell.border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      };
    });

    // Añadir datos
    rows.forEach(row => {
      const rowData: any[] = [];
      for (const key in columnMapping) {
        // Combinar 'apellido' y 'name' en una sola celda
        if (key === 'apellido' || key === 'name') {
          if (!rowData.some(data => data.includes(' '))) {  // Solo agregar si no se ha agregado ya
            const fullName = `${capitalize(row.name)} ${capitalize(row.apellido)}`;
            rowData.push(fullName);
          }
        } else if (columnMapping[key].toLowerCase().includes('fecha')) {
          rowData.push(dayjs(row[key]).format('DD/MM/YYYY'));
        } else if (typeof row[key] === 'boolean') {
          const value = row[key];
          rowData.push(value === true ? 'SI' : 'NO');
        } else {
          const value = row[key];
          rowData.push(typeof value === 'string' ? capitalize(value) : value);
        }
      }
      worksheet.addRow(rowData).eachCell(cell => {
        cell.border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' },
        };
      });
    });

    // Ajustar el ancho de las columnas al contenido
    worksheet.columns.forEach((column) => {
      if (column && typeof column.eachCell === 'function') {
        let maxColumnLength = 0;
        column.eachCell({ includeEmpty: true }, (cell, rowNumber) => {
          if (rowNumber <= 1) return;
          const cellValue = cell.value;
          const columnLength = (cellValue?.toString().length) || 0;
          if (columnLength > maxColumnLength) {
            maxColumnLength = columnLength;
          }
        });
        column.width = maxColumnLength + 2;  // +2 para un pequeño margen
      }
    });

    // Guardar archivo
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, 'datos.xlsx');
  };

  return (
    <GridToolbarContainer style={{ display: 'flex' }}>
      <Button variant='outlined' color='primary' onClick={exportToExcel} size='small' style={{ margin: 10 }}>Exportar a Excel</Button>
      <Button variant='outlined' color='error' onClick={eliminarTodos} size='small' style={{ margin: 10 }}>Eliminar todos</Button>

      <div style={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-end' }}>
        <GridToolbar
          csvOptions={{ disableToolbarButton: true }}
          printOptions={{ disableToolbarButton: true }}
          showQuickFilter
          quickFilterProps={{ debounceMs: 500 }}
        />
      </div>
    </GridToolbarContainer>
  );
};

export default CustomToolbar;